import { useMemo, useRef } from 'react';
import { toast, Id, ToastOptions } from 'react-toastify';
import { Toast } from 'modules/notifications/components/Toast';
import type { Variant, ToastProps, CombinedTypes } from 'modules/notifications/types';

const splitProps = (
  props: CombinedTypes,
  variant: Variant = 'info'
): [ToastProps, ToastOptions<ToastProps>] => {
  const { id, message, header, actions, onClose, isCode, messageList, noDuplicate, ...rest } =
    props;
  const toastId = noDuplicate ? id : `${id}-${Math.random()}`;
  return [
    { id: toastId, message, header, actions, variant, onClose, isCode, messageList },
    { ...rest, toastId, onClose: () => onClose && onClose('', toastId) },
  ];
};

const base = (props: CombinedTypes): Id => {
  const [toastProps, toastOptions] = splitProps(props, 'base');
  return toast<ToastProps>(Toast(toastProps), toastOptions);
};

const success = (props: CombinedTypes): Id => {
  const [toastProps, toastOptions] = splitProps(props, 'success');
  return toast.success<ToastProps>(Toast(toastProps), toastOptions);
};

const error = (props: CombinedTypes): Id => {
  const [toastProps, toastOptions] = splitProps(props, 'error');
  return toast.error<ToastProps>(Toast(toastProps), toastOptions);
};

const warning = (props: CombinedTypes): Id => {
  const [toastProps, toastOptions] = splitProps(props, 'warning');
  return toast.warning<ToastProps>(Toast(toastProps), toastOptions);
};

const info = (props: CombinedTypes): Id => {
  const [toastProps, toastOptions] = splitProps(props, 'info');
  return toast.info<ToastProps>(Toast(toastProps), toastOptions);
};

type UseNotificationReturn = {
  notify: {
    base: (props: CombinedTypes) => Id;
    success: (props: CombinedTypes) => Id;
    error: (props: CombinedTypes) => Id;
    warning: (props: CombinedTypes) => Id;
    info: (props: CombinedTypes) => Id;
  };
  dismissNotification: (toastId?: Id) => void;
};

/**
 * Toast hook, which should be used to display a toast, not the Toast component JSX
 * @param notify - open a toast
 *   - `success` - success styling, green background
 *   - `error` - error styling, red background
 *   - `warning` - warning styling, orange background
 *   - `info` - info styling, blue background
 *   - `base` - base styling, white background, not part of design system
 */
export const useNotification = (): UseNotificationReturn => {
  const ids = useRef<Id[]>([]);

  // Memoized so the returned object keeps a stable identity across renders. Consumers put
  // `notify` in dependency arrays — e.g. the compliance list's column builder — and a fresh
  // object every render invalidated those memos. A rebuilt TanStack `columns` array gives every
  // cell a new `flexRender` component type, which React remounts rather than updates, silently
  // tearing down whatever the user has open in a row (the kebab menu closing mid-click).
  // `ids` is a ref and every helper it closes over is module scope, so there are no real deps.
  return useMemo(() => {
    const onClose = (btn: string, id: string | number, props: CombinedTypes) => {
      if (ids.current.indexOf(id) > -1) {
        if (btn) {
          toast.dismiss(id);
        }
        ids.current = ids.current.filter(toastId => toastId !== id);
        if (props.onClose) {
          props.onClose(btn, id);
        }
      }
    };

    return {
      notify: {
        base: (props: CombinedTypes) => {
          const toastId = base({
            ...props,
            onClose: (btn, id) => onClose(btn, id, props),
          });
          ids.current.push(toastId);
          return toastId;
        },
        /**
         * Success toast
         * Note that this should be used with the notify hook, not with the Toast component
         * @param id - same id's will replace existing toasts
         * @param message - text to show in the toast
         * @param header - headline text to be shown in the toast
         * @param actions - buttons to show in the toast, default is a close button
         *   - `id` - unique identifier
         *   - `label` - text on the button
         *   - `onClick` - on click callback function
         * @param onClose - on close callback function
         * @param isCode - true if message is a localized code
         * @param autoClose - time in milliseconds before the toast automatically closes
         */
        success: (props: CombinedTypes) => {
          const toastId = success({
            ...props,
            onClose: (btn, id) => onClose(btn, id, props),
          });
          ids.current.push(toastId);
          return toastId;
        },
        /**
         * Error toast
         * Note that this should be used with the notify hook, not with the Toast component
         * @param id - same id's will replace existing toasts
         * @param message - text to show in the toast
         * @param header - heeadline text to shown in the toast
         * @param actions - buttons to show in the toast, default is a close button
         *   - `id` - unique identifier
         *   - `label` - text on the button
         *   - `onClick` - on click callback function
         * @param onClose - on close callback function
         * @param isCode - true if message is a localized code
         * @param autoClose - time in milliseconds before the toast automatically closes
         */
        error: (props: CombinedTypes) => {
          const toastId = error({
            ...props,
            onClose: (btn, id) => onClose(btn, id, props),
          });
          ids.current.push(toastId);
          return toastId;
        },
        /**
         * Warning toast
         * Note that this should be used with the notify hook, not with the Toast component
         * @param id - same id's will replace existing toasts
         * @param message - text to show in the toast
         * @param header - heeadline text to shown in the toast
         * @param actions - buttons to show in the toast, default is a close button
         *   - `id` - unique identifier
         *   - `label` - text on the button
         *   - `onClick` - on click callback function
         * @param onClose - on close callback function
         * @param isCode - true if message is a localized code
         * @param autoClose - time in milliseconds before the toast automatically closes
         */
        warning: (props: CombinedTypes) => {
          const toastId = warning({
            ...props,
            onClose: (btn, id) => onClose(btn, id, props),
          });
          ids.current.push(toastId);
          return toastId;
        },
        /**
         * Info toast
         * Note that this should be used with the notify hook, not with the Toast component
         * @param id - same id's will replace existing toasts
         * @param message - text to show in the toast
         * @param header - heeadline text to shown in the toast
         * @param actions - buttons to show in the toast, default is a close button
         *   - `id` - unique identifier
         *   - `label` - text on the button
         *   - `onClick` - on click callback function
         * @param onClose - on close callback function
         * @param isCode - true if message is a localized code
         * @param autoClose - time in milliseconds before the toast automatically closes
         */
        info: (props: CombinedTypes) => {
          const toastId = info({
            ...props,
            onClose: (btn, id) => onClose(btn, id, props),
          });
          ids.current.push(toastId);
          return toastId;
        },
      },
      dismissNotification: toast.dismiss,
    };
  }, []);
};
